import React, { useMemo, useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

/* ===== tokens ===== */
const TOK = {
  bg: "rgba(12,18,28,.78)",
  border: "1px solid rgba(255,255,255,.08)",
  text: "#e5e7eb",
  sub: "#94a3b8",
  sep: "rgba(255,255,255,.18)",
};

/* ===== radii (2x less) ===== */
const R = {
  card: 10,
  tab: 6,
  input: 6,
  sheet: 16,
};

/* ===== categories ===== */
const TOP_LEFT = ["Trending",  "New"];
const TOP_RIGHT = ["Politics", "Sports", "Finance", "Crypto", "Game", "Tech", "World", "Economy"];

/* ===== filter tree ===== */
const TREE = {
  Sports: {
    chips: ["Live", "Futures"],
    sub: [
      "American Football",
      "Basketball",
      "Hockey",
      "Tennis",
      "Esports",
      "Baseball",
      "Football",
      "Cricket",
      "Golf",
      "UFC",
      "Formula 1",
      "Chess",
      "Boxing",
    ],
  },
  Finance: {
    chips: ["All", "Daily", "Weekly", "Monthly"],
    sub: ["Stocks", "Earnings", "Indices", "Commodities", "Forex", "Acquisitions", "Earnings Calls", "IPOs", "Fed Rates"],
  },
  Crypto: {
    chips: ["All", "Pump", "Dump", "Flat", "Pre-Market", "ETF"],
    sub: ["Bitcoin", "Ethereum", "Solana", "XRP", "Dogecoin", "ALTCOIN", "Microstrategy", "MEME"],
  },
  Politics: {
    chips: [],
    sub: ["USA", "China", "Russia", "Venezuela", "Iran", "South Korea", "Ukraine", "Gaza", "Israel"],
  },
  Game: {
  chips: ["All", "PC", "Console", "Mobile", "Esports"],
  sub: ["Dota 2", "CS2", "LoL", "Valorant", "Fortnite", "GTA", "Minecraft"],
},

};

function getMeta(top) {
  const m = TREE[top];
  return {
    chips: Array.isArray(m?.chips) ? m.chips : [],
    sub: Array.isArray(m?.sub) ? m.sub : [],
  };
}

/* ✅ separator as Tab (so Tabs won't pass props into a DOM div) */
const SepTab = ({ value = "__sep" }) => (
  <Tab
    disabled
    value={value}
    label=""
    data-sep="1"
    sx={{
      minWidth: 10,
      width: 10,
      px: 0,
      mx: 0.6,
      cursor: "default",
      "&.Mui-disabled": { opacity: 1 },
      "& .MuiTouchRipple-root": { display: "none" },
      "&::after": {
        content: '""',
        display: "block",
        width: "1px",          // было 0.5px — на верхнем ряду часто “пропадает”
        height: 18,
        margin: "0 auto",
        background: TOK.sep,
        opacity: 1,            // было 0.9
        borderRadius: 99,
      },
    }}
  />
);

/* ===== styles ===== */
const topTabsSx = {
  minHeight: 44,
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTabs-scrollButtons": {
    color: TOK.sub,
    "&.Mui-disabled": { opacity: 0.2 },
  },

  // ✅ обычные табы
  '& .MuiTab-root:not([data-sep="1"])': {
    minHeight: 44,
    minWidth: "auto",
    textTransform: "none",
    fontWeight: 850,
    fontSize: 14,
    color: TOK.sub,
    px: 1.15,
    borderRadius: `${R.tab}px`,
    mx: 0.15,
    "&.Mui-selected": { color: TOK.text, background: "rgba(255,255,255,.06)" },
  },

  // ✅ сепаратор (чтобы его точно не “ломало” общим стилем)
  '& .MuiTab-root[data-sep="1"]': {
    minHeight: 44,
    padding: 0,
    margin: 0,
  },
};

const chipTabsSx = {
  minHeight: 34,
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTabs-scrollButtons": {
    color: TOK.sub,
    "&.Mui-disabled": { opacity: 0.2 },
  },
  "& .MuiTab-root": {
    minHeight: 34,
    minWidth: "auto",
    textTransform: "none",
    fontWeight: 900,
    fontSize: 12,
    color: "#cbd5e1",
    px: 0.95,
    borderRadius: `${R.tab}px`,
    mx: 0.15,
    background: "rgba(255,255,255,.04)",
    border: "1px solid rgba(255,255,255,.10)",
    "&.Mui-selected": {
      color: TOK.text,
      background: "rgba(56,189,248,.14)",
      borderColor: "rgba(56,189,248,.35)",
    },
  },
};

export default function Filters({
  searchQuery,
  setSearchQuery,
  marketTop,
  setMarketTop,
  marketChip,
  setMarketChip,
  marketSub,
  setMarketSub,
}) {
  const isMobile = useMediaQuery("(max-width: 900px)");
  const [sheetOpen, setSheetOpen] = useState(false);

  const meta = useMemo(() => getMeta(marketTop), [marketTop]);
  const showSecondary = marketTop !== "All" && (meta.chips.length > 0 || meta.sub.length > 0);

  const onAll = () => {
    setMarketTop("All");
    setMarketChip("All");
    setMarketSub("All");
    setSearchQuery("");
  };

  const onTopChange = (_e, v) => {
    if (!v) return;
    if (v === "All") return onAll();
    // ignore separators just in case
    if (String(v).startsWith("__sep")) return;
    setMarketTop(v);
    setMarketChip("All");
    setMarketSub("All");
  };

  const resetAll = () => {
    onAll();
    setSheetOpen(false);
  };

  /* ===== Desktop layout: inline mode/sub ===== */
  const DesktopSecondary = () =>
    showSecondary ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, flex: 1, minWidth: 0 }}>
        {/* chips */}
        {meta.chips.length ? (
          <Tabs
            value={marketChip}
            onChange={(_e, v) => {
              if (!v || String(v).startsWith("__sep")) return;
              setMarketChip(v);
              setMarketSub("All");
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              ...chipTabsSx,
              flex: "0 0 auto",
              minWidth: 0,
              maxWidth: "52%",
            }}
          >
            <Tab value="All" label="All" />
            {meta.chips.filter((c) => c !== "All").map((c) => (
              <Tab key={c} value={c} label={c} />
            ))}
          </Tabs>
        ) : null}

        {meta.chips.length && meta.sub.length ? <SepTab value="__sep_mid" /> : null}

        {/* sub */}
        {meta.sub.length ? (
          <Tabs
            value={marketSub}
            onChange={(_e, v) => {
              if (!v || String(v).startsWith("__sep")) return;
              setMarketSub(v);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              ...chipTabsSx,
              flex: "1 1 auto",
              minWidth: 0,
            }}
          >
            <Tab value="All" label="All" />
            {meta.sub.map((s) => (
              <Tab key={s} value={s} label={s} />
            ))}
          </Tabs>
        ) : null}
      </Box>
    ) : null;

  /* ===== Mobile layout: bottom sheet ===== */
  const MobileSheet = () => (
    <Drawer
      anchor="bottom"
      open={sheetOpen}
      onClose={() => setSheetOpen(false)}
      PaperProps={{
        sx: {
          bgcolor: "rgba(8,11,17,.98)",
          borderTopLeftRadius: `${R.sheet}px`,
          borderTopRightRadius: `${R.sheet}px`,
          borderTop: "1px solid rgba(255,255,255,.10)",
          p: 2,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography sx={{ fontWeight: 900, color: TOK.text, flex: 1 }}>Filters</Typography>
        <IconButton onClick={resetAll} size="small" sx={{ color: TOK.sub }}>
          <RestartAltIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => setSheetOpen(false)} size="small" sx={{ color: TOK.text }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Mode chips */}
      {showSecondary && meta.chips.length ? (
        <>
          <Typography sx={{ fontSize: 12, color: TOK.sub, mb: 0.75 }}>Mode</Typography>
          <Tabs
            value={marketChip}
            onChange={(_e, v) => {
              if (!v || String(v).startsWith("__sep")) return;
              setMarketChip(v);
              setMarketSub("All");
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ ...chipTabsSx, mb: 1.25 }}
          >
            {/* ✅ FIX: always include All even if meta.chips doesn't have it */}
            {["All", ...meta.chips.filter((c) => c !== "All")].map((c) => (
              <Tab key={c} value={c} label={c} />
            ))}
          </Tabs>
        </>
      ) : null}

      {/* Subfilters */}
      {showSecondary && meta.sub.length ? (
        <>
          <Typography sx={{ fontSize: 12, color: TOK.sub, mb: 0.75 }}>Subfilter</Typography>
          <Tabs
            value={marketSub}
            onChange={(_e, v) => {
              if (!v || String(v).startsWith("__sep")) return;
              setMarketSub(v);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ ...chipTabsSx }}
          >
            <Tab value="All" label="All" />
            {meta.sub.map((s) => (
              <Tab key={s} value={s} label={s} />
            ))}
          </Tabs>
        </>
      ) : (
        <Typography sx={{ fontSize: 12, color: TOK.sub }}>No subfilters for this category.</Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <Box onClick={() => setSheetOpen(false)} role="button" style={{ cursor: "pointer" }}>
          <Box
            sx={{
              height: 44,
              borderRadius: `${R.tab}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 900,
              color: "#0b1220",
              background: "linear-gradient(90deg, rgba(56,189,248,.95) 0%, rgba(20,241,149,.9) 100%)",
            }}
          >
            Done
          </Box>
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <>
      <Box
        sx={{
          borderRadius: `${R.card}px`,
          overflow: "hidden",
          background: TOK.bg,
          border: TOK.border,
          boxShadow: "0 10px 32px rgba(2,8,23,.35)",
          backdropFilter: "blur(10px)",
          mb: 1.2,
          mt: 0.5,
        }}
      >
        {/* ROW 1: categories (✅ only Tab children, separators are SepTab) */}
        <Tabs
          value={marketTop}
          onChange={onTopChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ ...topTabsSx, px: 1 }}
        >
          <Tab value="All" label="All" />
          {TOP_LEFT.map((t) => (
            <Tab key={t} value={t} label={t} />
          ))}
          <SepTab value="__sep_top" />
          {TOP_RIGHT.map((t) => (
            <Tab key={t} value={t} label={t} />
          ))}
        </Tabs>

        {/* ROW 2: desktop inline filters OR mobile compact */}
        <Box
          sx={{
            px: 1,
            pb: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            minWidth: 0,
          }}
        >
          <TextField
            placeholder="Search"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: TOK.sub, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: isMobile ? "100%" : 280,
              flex: isMobile ? "1 1 auto" : "0 0 auto",
              "& .MuiInputBase-root": {
                height: 34,
                color: TOK.text,
                background: "rgba(255,255,255,.04)",
                borderRadius: `${R.input}px`,
              },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.12)" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.22)" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(56,189,248,.5)" },
              "& .MuiInputBase-input::placeholder": { color: TOK.sub, opacity: 1 },
            }}
          />

          {isMobile ? (
            <IconButton
              onClick={() => setSheetOpen(true)}
              size="small"
              sx={{
                flexShrink: 0,
                width: 34,
                height: 34,
                borderRadius: `${R.input}px`,
                border: "1px solid rgba(255,255,255,.12)",
                background: "rgba(56,189,248,.10)",
                color: TOK.text,
                "&:hover": { background: "rgba(56,189,248,.14)" },
              }}
            >
              <TuneIcon fontSize="small" />
            </IconButton>
          ) : (
            <DesktopSecondary />
          )}
        </Box>
      </Box>

      {isMobile ? <MobileSheet /> : null}
    </>
  );
}

